import { MouseEvent, useCallback, useEffect, useState } from "react";
import useMenuStore from "src/store/useMenuStore";
import { MenuDto } from "src/dtos/menu/menuDto";
import useUiuxStore from "src/store/useUiuxStore";
import { actionBarMinWidthWrapper, blueButtonClass, BookMarkIcon, grayButtonClass, NonBookMarkIcon } from "src/components/common/common";
import { TabUtils } from "src/utils/menu/tabUtils";
import { getMenuDisplayNm } from "src/utils/menu/menuUtil";
import useActionbarAuthStore from "src/store/useActionbarAuthStore";
import { userBookmark } from "src/apis/userApi";
import useUserStore from "src/store/useUserStore";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { AuthactionbarLog } from "./AuthactionbarLog";
import { postProgramLog } from "src/apis/programLogApi";

interface AuthProps {
	queryAut?: string;
	insertAut?: string;
	deleteAut?: string;
	saveAut?: string;
	printAut?: string;
	excelAut?: string;
	initAut?: string;
	page?: number;
	allCount: number;
}

type ActionbarClickHandler = (e: MouseEvent<HTMLButtonElement>) => void | Promise<void>;

interface Props {
	onFetchClick?: () => void;
	onCreateClick?: ActionbarClickHandler;
	onDeleteClick?: () => void;
	onInitClick?: () => void;
	onSaveClick?: () => void;
	onPrintClick?: () => void;
	onExcelClick?: () => void;
	page?: number;
	customButtons?: React.ReactNode[];
}

const config = {
	env: process.env.REACT_APP_MODE,
}

const Actionbartest = ({ 
	onFetchClick, 
	onCreateClick, 
	onDeleteClick, 
	onInitClick, 
	onSaveClick, 
	onPrintClick, 
	onExcelClick,
	page = 1,
	customButtons = [],
}: Props) => {
	const { thirdMenuList, secondMenuList, menuList } = useMenuStore();
	const { tabList, activeTab, removeTabList, authactionbarLogModalVisible, setAuthactionbarLogModalVisible } = useUiuxStore();
	const { fetchBookmarkCdCode, bookmarkCdList } = useCommonCodeStore();
	const { userInfo } = useUserStore();
	const { handleExitClick } = TabUtils();
	const auth = useActionbarAuthStore((state) => state.auth);

	const [menu, setMenu] = useState<MenuDto>();
	const [secondMenu, setSecondMenu] = useState<MenuDto>();
	const [thirdMenu, setThirdMenu] = useState<MenuDto>();

	const [isBookmark, setIsBookmark] = useState(false);
	const [bookmarkTrigger, setBookmarkTrigger] = useState(false);



	const buttonClassName = (isDisabled?: boolean) => 
		`h-full text-white rounded-[4px] px-[16px] font-semibold ${
			isDisabled 
				? grayButtonClass
				: blueButtonClass
		}`;

	// 권한값을 불리언으로 변환하는 함수
	const isDisabled = (authValue?: string) => authValue !== "Y";

	useEffect(() => {
		if (!activeTab?.key) return;
		
		// 계층적 메뉴 구조에서 현재 활성 탭에 해당하는 메뉴 찾기 (모든 레벨 지원)
		let foundMenu: MenuDto | undefined;
		let foundSecondMenu: MenuDto | undefined;
		let foundFirstMenu: MenuDto | undefined;
		
		// 재귀적으로 메뉴를 찾는 함수
		const findMenuInHierarchy = (menus: MenuDto[], targetKey: string, parentMenu?: MenuDto, grandParentMenu?: MenuDto): boolean => {
			for (const menu of menus) {
				if (menu.menuCd === targetKey) {
					foundMenu = menu;
					if (menu.menuLevel === 2) {
						foundSecondMenu = menu;
						foundFirstMenu = parentMenu;
					} else if (menu.menuLevel === 3) {
						foundSecondMenu = parentMenu;
						foundFirstMenu = grandParentMenu;
					} else if (menu.menuLevel === 1) {
						foundFirstMenu = menu;
					}
					return true;
				}
				
				// 하위 메뉴에서 재귀적으로 찾기
				if (menu.subMenu && menu.subMenu.length > 0) {
					if (findMenuInHierarchy(menu.subMenu, targetKey, menu, parentMenu)) {
						return true;
					}
				}
			}
			return false;
		};
		
		// 1차 메뉴부터 시작하여 모든 레벨에서 찾기
		findMenuInHierarchy(menuList, activeTab.key);
		
		if (foundMenu) {
			setThirdMenu(foundMenu);
			setSecondMenu(foundSecondMenu);
			setMenu(foundFirstMenu);
		}
	}, [activeTab, menuList])

	const patchBookmark = useCallback(async () => {
		if (!thirdMenu?.menuCd) {
			console.error("즐겨찾기 추가 실패: 메뉴 코드 또는 사용자 ID가 없습니다.");
			return;
		}
		
		try {
			await userBookmark(thirdMenu.menuCd);
			fetchBookmarkCdCode();
		} catch (error) {
			console.error("즐겨찾기 API 호출 실패:", error);
		}
	}, [thirdMenu]);

	const logAction = useCallback((actionType: string) => {
		if (!thirdMenu?.menuCd) return;
		postProgramLog({ programId: thirdMenu.menuCd, actionType });
	}, [thirdMenu?.menuCd]);

	const getManualHelpUrls = useCallback(() => {
		const manualPageFromMenu = thirdMenu?.manualPage;
		const manualPageNumber =
			typeof manualPageFromMenu === 'number' && manualPageFromMenu > 0
				? manualPageFromMenu
				: page;

		const manualBaseUrl = 'http://helpmes.raonzena.co.kr';
		const manualUrl = `${manualBaseUrl}#page=${manualPageNumber}`;
		return { manualBaseUrl, manualUrl };
	}, [thirdMenu?.manualPage, page]);

	const { manualUrl } = getManualHelpUrls();

	useEffect(() => {
		if(bookmarkTrigger) {
			patchBookmark();
			setBookmarkTrigger(false);
		}
		if(bookmarkCdList.map((item) => item.key).includes(thirdMenu?.menuCd || '')) {
			setIsBookmark(true);
		} else {
			setIsBookmark(false);
		}
	}, [bookmarkCdList, bookmarkTrigger, patchBookmark]);

	return <div className={`h-[60px] flex flex-col px-[12px] py-[4px] bg-blue-lightest justify-center overflow-x-auto ${actionBarMinWidthWrapper}`}>
		<p className="">
		<span className="float-left text-black-900 font-bold">{getMenuDisplayNm(thirdMenu?.menuCd || '', thirdMenu?.menuNm || '')} / {thirdMenu?.menuCd}</span>
		<span className="float-right text-gray-500">
			{menu?.menuNm} 
			{secondMenu?.menuNm && ` > ${secondMenu.menuNm}`}
			{thirdMenu?.menuNm && thirdMenu.menuLevel > 2 && ` > ${getMenuDisplayNm(thirdMenu.menuCd, thirdMenu.menuNm)}`}
		</span>
		</p>
		<div className="flex-1 flex items-center justify-between">
			<div className={`w-full h-[80%] flex items-center justify-between`}>
				<div className="flex h-full items-center gap-[8px]">
					<button 
						className={buttonClassName(isDisabled(auth?.queryAut))}
						onClick={() => { logAction('SELECT'); onFetchClick?.(); }}
						disabled={isDisabled(auth?.queryAut)}>
						조회
					</button>
					<button 
						className={buttonClassName(isDisabled(auth?.insertAut))}
						onClick={(e) => { logAction('INSERT'); onCreateClick?.(e); }}
						disabled={isDisabled(auth?.insertAut)}>
						등록
					</button>
					<button 
						className={buttonClassName(isDisabled(auth?.deleteAut))}
						onClick={() => { logAction('DELETE'); onDeleteClick?.(); }}
						disabled={isDisabled(auth?.deleteAut)}>
						삭제
					</button>
					<button 
						className={buttonClassName(isDisabled(auth?.saveAut))}
						onClick={() => { logAction('SAVE'); onSaveClick?.(); }}
						disabled={isDisabled(auth?.saveAut)}>
						저장
					</button>
					<button 
						className={buttonClassName(isDisabled(auth?.printAut))}
						onClick={() => { logAction('PRINT'); onPrintClick?.(); }}
						disabled={isDisabled(auth?.printAut)}>
						출력
					</button>
					<button 
						className={buttonClassName(isDisabled(auth?.excelAut))}
						onClick={() => { logAction('EXCEL'); onExcelClick?.(); }}
						disabled={isDisabled(auth?.excelAut)}>
						Excel
					</button>
					<button 
						className={buttonClassName(false)}
						onClick={onInitClick}
						disabled={false}>
						초기화 
					</button>
					<button 
						className={`h-full text-white rounded-[4px] bg-blue-dark px-[16px] font-semibold hover:bg-blue-600`}
						onClick={() => {
							handleExitClick(activeTab?.key!);
						}}>
						닫기
					</button>
					<button 
						className="h-full px-2"
						onClick={() => {
							setIsBookmark(!isBookmark);
							setBookmarkTrigger(true);
						}}
					>
						{isBookmark ? (
							<BookMarkIcon className="h-6 w-6 text-blue-600" />
						) : (
							<NonBookMarkIcon className="h-6 w-6 text-gray-400" />
						)}
					</button>
					{/* 커스텀 버튼들 */}
					{customButtons.map((button, index) => (
						<div key={`custom-button-${index}`} className="h-full">
							{button}
						</div>
					))}
				</div>
				<button className={`h-full text-white rounded-[4px] bg-blue-dark px-[16px] font-semibold hover:bg-blue-600`}
					onClick={() => {
						window.open(manualUrl, '_blank');
					}}>
					도움말 ?
				</button>
				{userInfo?.autGroupCd === "ADMIN" && config.env === "dev" && (
					<button 
						className={`h-full text-white rounded-[4px] bg-blue-dark px-[16px] font-semibold hover:bg-blue-600`}
						onClick={() => {
							setAuthactionbarLogModalVisible(!authactionbarLogModalVisible);
						}}
					>
						로그 보기
					</button>
				)}
			</div>
		</div>
	</div>
}

export default Actionbartest;